import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, CircularProgress, Alert,
  Tooltip, Tabs, Tab, MenuItem, Select, FormControl, InputLabel,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Block as BlockIcon,
  CheckCircle as ActiveIcon, Settings as SettingsIcon,
  AccountBalanceWallet as WalletIcon, Refresh as RefreshIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import adminService from '../../services/adminService';

const AdminLandlords = () => {
  const [landlords, setLandlords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [configTab, setConfigTab] = useState(0);
  const [selectedLandlord, setSelectedLandlord] = useState(null);
  const [saving, setSaving] = useState(false);
  const [balanceInputs, setBalanceInputs] = useState({});

  const [createForm, setCreateForm] = useState({ username: '', email: '', full_name: '', phone: '', password: '' });
  const [editForm, setEditForm] = useState({ full_name: '', phone: '' });
  const [pwForm, setPwForm] = useState({ new_password: '', confirm: '' });
  const [mpesaForm, setMpesaForm] = useState({ consumer_key: '', consumer_secret: '', shortcode: '', passkey: '', environment: 'production', callback_url: '', validation_url: '', confirmation_url: '' });
  const [smsForm, setSmsForm] = useState({ provider: 'hostpinnacle', api_key: '', username: '', password: '', sender_id: '' });
  const [commForm, setCommForm] = useState({ commission_type: 'percentage', commission_value: 2, is_active: true });

  const fetchLandlords = useCallback(() => {
    setLoading(true);
    adminService.getLandlords().then(setLandlords).catch(() => setError('Failed to load landlords')).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchLandlords(); }, [fetchLandlords]);

  const handleCreate = async () => {
    setSaving(true);
    try { await adminService.createLandlord(createForm); toast.success('Landlord created'); setCreateOpen(false); setCreateForm({ username: '', email: '', full_name: '', phone: '', password: '' }); fetchLandlords(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to create'); }
    finally { setSaving(false); }
  };

  const handleEdit = async () => {
    setSaving(true);
    try { await adminService.updateLandlord(selectedLandlord.id, editForm); toast.success('Updated'); setEditOpen(false); fetchLandlords(); }
    catch { toast.error('Failed to update'); }
    finally { setSaving(false); }
  };

  const handleToggleActive = async (landlord) => {
    try { await adminService.updateLandlord(landlord.id, { is_active: !landlord.is_active }); toast.success(`Landlord ${landlord.is_active ? 'deactivated' : 'activated'}`); fetchLandlords(); }
    catch { toast.error('Failed'); }
  };

  const openConfigure = async (landlord) => {
    setSelectedLandlord(landlord); setConfigTab(0); setPwForm({ new_password: '', confirm: '' });
    const [mpesa, sms, comm] = await Promise.allSettled([
      adminService.getLandlordMpesaConfig(landlord.id),
      adminService.getLandlordSmsConfig(landlord.id),
      adminService.getLandlordCommission(landlord.id),
    ]);
    if (mpesa.status === 'fulfilled' && mpesa.value.configured) {
      const m = mpesa.value;
      setMpesaForm({
        consumer_key: m.consumer_key || '', consumer_secret: m.consumer_secret || '',
        shortcode: m.shortcode || '', passkey: m.passkey || '',
        environment: m.environment || 'production',
        callback_url: m.callback_url || '', validation_url: m.validation_url || '',
        confirmation_url: m.confirmation_url || '',
        _source: m.source || 'database',
        _note: m.note || null,
      });
    } else setMpesaForm({ consumer_key: '', consumer_secret: '', shortcode: '', passkey: '', environment: 'production', callback_url: '', validation_url: '', confirmation_url: '', _source: 'none', _note: null });
    if (sms.status === 'fulfilled' && sms.value.configured)
      setSmsForm({ provider: sms.value.provider || 'hostpinnacle', api_key: sms.value.api_key || '', username: sms.value.username || '', password: '', sender_id: sms.value.sender_id || '' });
    else setSmsForm({ provider: 'hostpinnacle', api_key: '', username: '', password: '', sender_id: '' });
    if (comm.status === 'fulfilled' && comm.value.override)
      setCommForm({ commission_type: comm.value.commission_type, commission_value: comm.value.commission_value, is_active: comm.value.is_active });
    else setCommForm({ commission_type: 'percentage', commission_value: 2, is_active: true });
    setConfigOpen(true);
  };

  const handleResetPassword = async () => {
    if (pwForm.new_password !== pwForm.confirm) { toast.error('Passwords do not match'); return; }
    if (pwForm.new_password.length < 6) { toast.error('Minimum 6 characters'); return; }
    setSaving(true);
    try { await adminService.resetLandlordPassword(selectedLandlord.id, pwForm.new_password); toast.success('Password reset'); setPwForm({ new_password: '', confirm: '' }); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleSaveMpesa = async () => { setSaving(true); try { await adminService.saveLandlordMpesaConfig(selectedLandlord.id, mpesaForm); toast.success('M-Pesa config saved'); } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); } finally { setSaving(false); } };
  const handleSaveSms = async () => { setSaving(true); try { await adminService.saveLandlordSmsConfig(selectedLandlord.id, smsForm); toast.success('SMS config saved'); } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); } finally { setSaving(false); } };
  const handleSaveCommission = async () => { setSaving(true); try { await adminService.saveLandlordCommission(selectedLandlord.id, commForm); toast.success('Commission saved'); } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); } finally { setSaving(false); } };

  const handleUpdateBalance = async (landlordId) => {
    const balance = parseFloat(balanceInputs[landlordId]);
    if (isNaN(balance) || balance < 0) { toast.error('Enter a valid balance'); return; }
    try { await adminService.updateMpesaBalance(landlordId, balance); toast.success('Balance updated'); setBalanceInputs(prev => ({ ...prev, [landlordId]: '' })); fetchLandlords(); }
    catch { toast.error('Failed to update balance'); }
  };

  if (loading) return <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="bold">Manage Landlords</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>Add Landlord</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              {['Name','Email','Phone','Tenants','Rooms','M-Pesa Balance','Status','Actions'].map(h => <TableCell key={h}><strong>{h}</strong></TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {landlords.map((l) => (
              <TableRow key={l.id} hover>
                <TableCell>{l.full_name}</TableCell>
                <TableCell>{l.email}</TableCell>
                <TableCell>{l.phone}</TableCell>
                <TableCell>{l.tenant_count}</TableCell>
                <TableCell>{l.room_count}</TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    {l.last_balance != null
                      ? <Chip icon={<WalletIcon />} label={`KES ${Number(l.last_balance).toLocaleString()}`} size="small" color="success" variant="outlined" />
                      : <Typography variant="caption" color="text.secondary">Not set</Typography>}
                    <TextField size="small" type="number" placeholder="Update" sx={{ width: 110 }}
                      value={balanceInputs[l.id] || ''}
                      onChange={(e) => setBalanceInputs(prev => ({ ...prev, [l.id]: e.target.value }))}
                      InputProps={{ startAdornment: <InputAdornment position="start">KES</InputAdornment> }} />
                    <IconButton size="small" onClick={() => handleUpdateBalance(l.id)}><RefreshIcon fontSize="small" /></IconButton>
                  </Box>
                </TableCell>
                <TableCell><Chip label={l.is_active ? 'Active' : 'Inactive'} color={l.is_active ? 'success' : 'default'} size="small" /></TableCell>
                <TableCell>
                  <Tooltip title="Configure (Password/M-Pesa/SMS/Commission)">
                    <IconButton size="small" color="primary" onClick={() => openConfigure(l)}><SettingsIcon fontSize="small" /></IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => { setSelectedLandlord(l); setEditForm({ full_name: l.full_name, phone: l.phone }); setEditOpen(true); }}><EditIcon fontSize="small" /></IconButton>
                  </Tooltip>
                  <Tooltip title={l.is_active ? 'Deactivate' : 'Activate'}>
                    <IconButton size="small" onClick={() => handleToggleActive(l)} color={l.is_active ? 'error' : 'success'}>
                      {l.is_active ? <BlockIcon fontSize="small" /> : <ActiveIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {landlords.length === 0 && <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}>No landlords. Click "Add Landlord".</TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Landlord</DialogTitle>
        <DialogContent>
          {['full_name','username','email','phone','password'].map(f => (
            <TextField key={f} fullWidth margin="normal" label={f.replace('_',' ').replace(/\b\w/g,c=>c.toUpperCase())} type={f==='password'?'password':'text'} value={createForm[f]} onChange={e=>setCreateForm({...createForm,[f]:e.target.value})} />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving}>{saving?<CircularProgress size={20}/>:'Create'}</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Landlord</DialogTitle>
        <DialogContent>
          <TextField fullWidth margin="normal" label="Full Name" value={editForm.full_name} onChange={e=>setEditForm({...editForm,full_name:e.target.value})} />
          <TextField fullWidth margin="normal" label="Phone" value={editForm.phone} onChange={e=>setEditForm({...editForm,phone:e.target.value})} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEdit} disabled={saving}>{saving?<CircularProgress size={20}/>:'Save'}</Button>
        </DialogActions>
      </Dialog>

      {/* Configure Dialog */}
      <Dialog open={configOpen} onClose={() => setConfigOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Configure: {selectedLandlord?.full_name}</DialogTitle>
        <Tabs value={configTab} onChange={(_,v) => setConfigTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="Password" /><Tab label="M-Pesa" /><Tab label="SMS" /><Tab label="Commission" />
        </Tabs>
        <DialogContent dividers>
          {/* Password Reset */}
          {configTab === 0 && <Box>
            <TextField fullWidth margin="normal" label="New Password" type="password" value={pwForm.new_password} onChange={e=>setPwForm({...pwForm,new_password:e.target.value})} />
            <TextField fullWidth margin="normal" label="Confirm Password" type="password" value={pwForm.confirm} onChange={e=>setPwForm({...pwForm,confirm:e.target.value})} />
            <Button variant="contained" fullWidth sx={{mt:1}} onClick={handleResetPassword} disabled={saving}>{saving?<CircularProgress size={20}/>:'Reset Password'}</Button>
          </Box>}

          {/* M-Pesa Config */}
          {configTab === 1 && <Box>
            {mpesaForm._source === 'env' && <Alert severity="info" sx={{mb:1}}>Showing existing global config from server. Enter the actual values and click Save to store them for this landlord.</Alert>}
            {mpesaForm._source === 'database' && <Alert severity="success" sx={{mb:1}}>Config saved in database for this landlord. Masked fields — re-enter to update.</Alert>}
            {['consumer_key','consumer_secret','shortcode','passkey'].map(f=><TextField key={f} fullWidth margin="dense" label={f.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())} value={mpesaForm[f]} onChange={e=>setMpesaForm({...mpesaForm,[f]:e.target.value})} />)}
            <FormControl fullWidth margin="dense"><InputLabel>Environment</InputLabel>
              <Select value={mpesaForm.environment} label="Environment" onChange={e=>setMpesaForm({...mpesaForm,environment:e.target.value})}>
                <MenuItem value="sandbox">Sandbox</MenuItem><MenuItem value="production">Production</MenuItem>
              </Select>
            </FormControl>
            {['callback_url','validation_url','confirmation_url'].map(f=><TextField key={f} fullWidth margin="dense" label={f.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())} value={mpesaForm[f]} onChange={e=>setMpesaForm({...mpesaForm,[f]:e.target.value})} />)}
            <Button variant="contained" fullWidth sx={{mt:2}} onClick={handleSaveMpesa} disabled={saving}>{saving?<CircularProgress size={20}/>:'Save M-Pesa Config'}</Button>
          </Box>}

          {/* SMS Config */}
          {configTab === 2 && <Box>
            <FormControl fullWidth margin="dense"><InputLabel>Provider</InputLabel>
              <Select value={smsForm.provider} label="Provider" onChange={e=>setSmsForm({...smsForm,provider:e.target.value})}>
                <MenuItem value="hostpinnacle">HostPinnacle (Comsomanet)</MenuItem>
                <MenuItem value="africastalking">Africa's Talking</MenuItem>
              </Select>
            </FormControl>
            {['api_key','username','sender_id'].map(f=><TextField key={f} fullWidth margin="dense" label={f.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())} value={smsForm[f]} onChange={e=>setSmsForm({...smsForm,[f]:e.target.value})} />)}
            <TextField fullWidth margin="dense" label="Password" type="password" value={smsForm.password} onChange={e=>setSmsForm({...smsForm,password:e.target.value})} />
            <Button variant="contained" fullWidth sx={{mt:2}} onClick={handleSaveSms} disabled={saving}>{saving?<CircularProgress size={20}/>:'Save SMS Config'}</Button>
          </Box>}

          {/* Commission */}
          {configTab === 3 && <Box>
            <Alert severity="info" sx={{mb:2}}>Overrides global commission for this landlord only.</Alert>
            <FormControl fullWidth margin="dense"><InputLabel>Type</InputLabel>
              <Select value={commForm.commission_type} label="Type" onChange={e=>setCommForm({...commForm,commission_type:e.target.value})}>
                <MenuItem value="percentage">Percentage (%)</MenuItem>
                <MenuItem value="flat">Flat Amount (KES)</MenuItem>
              </Select>
            </FormControl>
            <TextField fullWidth margin="dense" type="number" label={commForm.commission_type==='percentage'?'Rate (%)':'Amount (KES)'} value={commForm.commission_value} onChange={e=>setCommForm({...commForm,commission_value:parseFloat(e.target.value)||0})} inputProps={{min:0,step:0.1}} />
            <Button variant="contained" fullWidth sx={{mt:2}} onClick={handleSaveCommission} disabled={saving}>{saving?<CircularProgress size={20}/>:'Save Commission'}</Button>
          </Box>}
        </DialogContent>
        <DialogActions><Button onClick={() => setConfigOpen(false)}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminLandlords;
